// src/modules/portal/components/TenantCard.tsx
// Card de empresa para listagens no portal

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Phone, MessageCircle, ExternalLink } from 'lucide-react';
import { TenantListingCard } from '../../../core/types/portal';
import { StarRating } from './StarRating';
import { LegendBadge } from '../../../components/LegendBadge';
import { storageHelper } from '../../../core/utils/storageHelper';

interface TenantCardProps {
  tenant: TenantListingCard;
  variant?: 'default' | 'compact' | 'featured';
}

export const TenantCard: React.FC<TenantCardProps> = ({ tenant, variant = 'default' }) => {
  const navigate = useNavigate();
  
  const handleCardClick = () => {
    navigate(`/empresas/${tenant.slug}`);
  };
  
  const handleWhatsAppClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (tenant.whatsappNumber) {
      const cleanNumber = tenant.whatsappNumber.replace(/\D/g, '');
      window.open(`https://wa.me/${cleanNumber}`, '_blank');
    }
  };

  const getTenantTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      'pousada': 'Pousada',
      'hotel': 'Hotel',
      'barco-hotel': 'Barco Hotel',
      'guia': 'Guia',
      'agencia': 'Agência',
      'loja': 'Loja',
      'influenciador': 'Influenciador',
      'associacao': 'Associação'
    };
    return labels[type] || type;
  };

  if (variant === 'compact') {
    return (
      <div 
        onClick={handleCardClick}
        className="flex items-start gap-4 p-4 bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer"
      >
        {/* Imagem */}
        <div className="relative flex-shrink-0">
          {tenant.coverImage && (
            <img 
              src={storageHelper.getResizedImageUrl(tenant.coverImage, '200x200')} 
              alt={tenant.businessName}
              loading="lazy"
              className="w-24 h-24 object-cover rounded-md"
            />
          )}
          {tenant.isFishingLegend && (
            <div className="absolute -top-2 -left-2 scale-75 origin-top-left">
              <LegendBadge level={tenant.legendTier || 'influencer'} showText={false} />
            </div>
          )}
        </div>
        
        {/* Conteúdo */}
        <div className="flex-grow min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-bold text-gray-800 truncate">{tenant.businessName}</h3>
            {tenant.averageRating && (
              <div className="flex items-center gap-1 flex-shrink-0">
                <StarRating rating={tenant.averageRating} size={14} />
                <span className="text-sm font-bold text-gray-700">{tenant.averageRating.toFixed(1)}</span>
              </div>
            )}
          </div>
          
          <p className="text-sm text-gray-500 mb-2">
            {getTenantTypeLabel(tenant.tenantType)} • {tenant.location.city}, {tenant.location.state}
          </p>
          
          <p className="text-sm text-gray-600 line-clamp-2">{tenant.shortDescription}</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      onClick={handleCardClick}
      className={`
        bg-white rounded-xl overflow-hidden border-2 transition-all duration-300 cursor-pointer
        hover:shadow-xl hover:border-brand-blue-medium group relative
        ${tenant.isFishingLegend 
          ? 'border-amber-400 shadow-[0_0_20px_rgba(251,191,36,0.15)] ring-1 ring-amber-400/30' 
          : tenant.isFeatured ? 'border-brand-yellow shadow-md' : 'border-gray-200'}
        ${variant === 'featured' ? 'md:flex md:flex-row' : ''}
      `}
    >
      {/* Legend Badge Overlay */}
      {tenant.isFishingLegend && (
        <div className="absolute top-3 left-3 z-20">
          <LegendBadge level={tenant.legendTier || 'legend'} />
        </div>
      )}
      {/* Badge de Destaque */}
      {tenant.isFeatured && (
        <div className="absolute top-3 left-3 z-10">
          <span className="bg-yellow-400 text-yellow-900 text-xs font-bold px-3 py-1 rounded-full">
            ⭐ Destaque
          </span>
        </div>
      )}
      
      {/* Badge Verificado */}
      {tenant.verifiedBadge && (
        <div className="absolute top-3 right-3 z-10">
          <span className="bg-brand-blue-medium text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
            ✓ Verificado
          </span>
        </div>
      )}
      
      {/* Imagem */}
      <div className={`relative ${variant === 'featured' ? 'md:w-1/3' : ''}`}>
        {tenant.coverImage ? (
          <img 
            src={storageHelper.getResizedImageUrl(tenant.coverImage, '800x800')} 
            alt={tenant.businessName}
            loading="lazy"
            className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-200"
          />
        ) : (
          <div className="w-full h-48 bg-gradient-to-br from-brand-blue-light to-brand-blue-medium flex items-center justify-center">
            <span className="text-6xl">🎣</span>
          </div>
        )}
        
        {/* Tipo de empresa */}
        <div className="absolute bottom-3 left-3">
          <span className="bg-brand-blue-deep/90 backdrop-blur-md text-white border border-white/20 text-xs font-semibold px-3 py-1 rounded-full shadow-sm">
            {getTenantTypeLabel(tenant.tenantType)}
          </span>
        </div>
      </div>
      
      {/* Conteúdo */}
      <div className={`p-5 ${variant === 'featured' ? 'md:w-2/3' : ''}`}>
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-grow">
            <h3 className={`text-xl font-bold mb-1 transition-colors ${tenant.isFishingLegend ? 'text-amber-600' : 'text-brand-blue-deep group-hover:text-brand-orange'}`}>
              {tenant.businessName}
            </h3>
            
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <MapPin size={14} className="text-brand-blue-medium" />
              <span>{tenant.location.city}, {tenant.location.state}</span>
            </div>
          </div>
          
          {/* Avaliação */}
          {tenant.averageRating && (
            <div className="flex flex-col items-end">
              <div className="flex items-center gap-1">
                <StarRating rating={tenant.averageRating} size={16} />
                <span className="font-bold text-brand-blue-deep">{tenant.averageRating.toFixed(1)}</span>
              </div>
              {tenant.reviewCount && (
                <span className="text-[10px] text-gray-500 font-medium">{tenant.reviewCount} avaliações</span>
              )}
            </div>
          )}
        </div>
        
        {/* Descrição */}
        <p className="text-gray-600 text-sm mb-4 line-clamp-2">
          {tenant.shortDescription}
        </p>
        
        {/* Destaques */}
        {tenant.highlights && tenant.highlights.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {tenant.highlights.slice(0, 3).map((highlight, idx) => (
              <span key={idx} className="bg-brand-blue-light text-brand-blue-deep text-xs px-2 py-1 rounded">
                {highlight}
              </span>
            ))}
          </div>
        )}
        
        {/* Tags */}
        {tenant.categoryTags && tenant.categoryTags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {tenant.categoryTags.slice(0, 3).map((tag, idx) => (
              <span key={idx} className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded">
                #{tag.replace(/-/g, ' ')}
              </span>
            ))}
          </div>
        )}
        
        {/* Preço */}
        {tenant.priceRange && (
          <div className="mb-4">
            <span className="text-sm text-gray-500">A partir de </span>
            <span className="text-lg font-bold text-green-600">
              {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(tenant.priceRange.min)}
            </span>
            <span className="text-sm text-gray-500"> /dia</span>
          </div>
        )}
        
        {/* Ações */}
        <div className="flex gap-2">
          {tenant.whatsappNumber && (
            <button
              onClick={handleWhatsAppClick}
              className="flex-1 bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors"
            >
              <MessageCircle size={18} />
              WhatsApp
            </button>
          )}
          
          <button
             onClick={handleCardClick}
             className="flex-1 bg-brand-blue-medium hover:bg-brand-blue-dark text-white font-semibold py-2 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors"
           >
             Ver Detalhes
             <ExternalLink size={18} />
           </button>
        </div>
      </div>
    </div>
  );
};
